import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/authContext';
import { useDefaultRoute } from '@/hooks/useDefaultRoute';
import { CircularProgress } from '@/components/ui/circular-progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string | string[];
  requiredPermission?: { resource: string; action: string };
  fallbackPath?: string;
  requireAll?: boolean; // For multiple roles, require all instead of any
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  fallbackPath = '/login',
  requireAll = false
}) => {
  const { isAuthenticated, user, isLoading, hasPermission, hasRole } = useAuth();
  const location = useLocation();
  const defaultRoute = useDefaultRoute();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <CircularProgress className="w-12 h-12 text-primary" />
          <div>
            <p className="text-lg font-medium text-foreground">Loading...</p>
            <p className="text-sm text-muted-foreground">Verifying your credentials</p>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to={fallbackPath}
        state={{ from: location, requiredRole, requiredPermission }}
        replace
      />
    );
  }

  // Check if user account is active
  if (user?.status !== 'active') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full">
          <Alert className="border-warning/50 bg-warning/5">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <AlertDescription className="text-warning">
              <div className="space-y-2">
                <p className="font-medium">Account Not Active</p>
                <p className="text-sm">
                  Your account is currently {user?.status}. Please contact support or check your email for activation instructions.
                </p>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Check role-based access — redirect to first permitted route
  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    const hasRequiredRole = requireAll
      ? roles.every(role => hasRole(role))
      : roles.some(role => hasRole(role));

    if (!hasRequiredRole) {
      return <Navigate to={defaultRoute} replace />;
    }
  }

  // Check permission-based access — redirect to first permitted route
  if (requiredPermission && !hasPermission(requiredPermission.resource, requiredPermission.action)) {
    return <Navigate to={defaultRoute} replace />;
  }

  // User is authenticated and has required permissions
  return <>{children}</>;
};

export default ProtectedRoute;
